// Real injected-wallet on-chain layer (MetaMask EVM + Phantom Solana).
// No WalletConnect. Falls back handled by callers when providers are absent.
import {
  createWalletClient, createPublicClient, custom, defineChain, getAddress, parseUnits, parseEther,
  erc20Abi,
} from "viem";
import { mainnet, base, arbitrum, optimism, bsc, polygon, avalanche, hyperliquid } from "viem/chains";
import { PublicKey, Transaction, SystemProgram, LAMPORTS_PER_SOL } from "@solana/web3.js";
import {
  getAssociatedTokenAddress, createTransferCheckedInstruction, TOKEN_PROGRAM_ID,
} from "@solana/spl-token";
import {
  getSolanaMintDecimals as apiSolMintDecimals, getSolanaBlockhash as apiSolBlockhash,
  apiSolBalance, apiSolTokenBalance,
  pricePreview,
  getPortfolioBalance,
} from "@/lib/api";
import { toast } from "sonner";

export const robinhoodChain = defineChain({
  id: 4663,
  name: "Robinhood Chain",
  nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
  rpcUrls: { default: { http: ["https://rpc.mainnet.chain.robinhood.com"] } },
  blockExplorers: {
    default: { name: "Robinhood Chain Explorer", url: "https://robinhoodchain.blockscout.com" },
  },
});

export const VIEM_CHAINS = {
  ethereum: mainnet, base, arbitrum, optimism, bnb: bsc, polygon, avalanche,
  hyperliquid, // HyperEVM (chainId 999) — native gas coin is HYPE
  robinhood: robinhoodChain,
};

export const hasInjectedEvm = () => !!defaultEvmProvider();
export const hasInjectedSolana = () =>
  typeof window !== "undefined" && !!(window.phantom?.solana || window.solana?.isPhantom || window.solflare);

// EIP-6963 multi-wallet discovery + legacy fallback.
// Phantom's EVM provider is a first-class wallet here: users running Phantom
// as their main wallet must be able to pick it for EVM payments too.
export function discoverEvmProviders() {
  return new Promise((resolve) => {
    if (typeof window === "undefined") return resolve([]);
    const found = new Map();
    const onAnnounce = (event) => {
      const d = event.detail;
      if (d?.info?.uuid && d?.provider?.request) {
        found.set(d.info.uuid, d);
      }
    };
    window.addEventListener("eip6963:announceProvider", onAnnounce);
    window.dispatchEvent(new Event("eip6963:requestProvider"));
    setTimeout(() => {
      window.removeEventListener("eip6963:announceProvider", onAnnounce);
      const legacy = window.ethereum;
      const list = Array.isArray(legacy?.providers) ? legacy.providers : legacy ? [legacy] : [];
      list.forEach((p, i) => {
        if (!p?.request) return;
        const dup = [...found.values()].some((x) => x.provider === p);
        if (!dup) {
          const name = p.isPhantom ? "Phantom"
            : p.isMetaMask ? "MetaMask" : p.isCoinbaseWallet ? "Coinbase Wallet"
            : p.isTrust ? "Trust Wallet" : p.isRainbow ? "Rainbow" : "Browser Wallet";
          found.set(`legacy-${i}`, { info: { uuid: `legacy-${i}`, name, icon: "", rdns: "legacy" }, provider: p });
        }
      });
      resolve([...found.values()]);
    }, 300);
  });
}

export function identifyInjectedWallets() {
  const e = typeof window !== "undefined" ? window.ethereum : null;
  const rh = identifyRobinhoodWallet();
  return {
    metamask: !!e?.isMetaMask,
    coinbase: !!e?.isCoinbaseWallet,
    trust: !!e?.isTrust,
    robinhood: rh.available,
    phantom: !!(window.phantom?.solana || window.solana?.isPhantom),
    solflare: !!window.solflare,
    hasEvm: !!defaultEvmProvider(),
    hasSolana: !!(window.phantom?.solana || window.solflare || window.solana),
  };
}

/** Detect Robinhood Wallet when injected (EIP-6963 or legacy flags). */
export function identifyRobinhoodWallet() {
  if (typeof window === "undefined") return { available: false, provider: null, name: null };
  const injected = window.ethereum;
  const list = Array.isArray(injected?.providers) ? injected.providers : injected ? [injected] : [];
  for (const p of list) {
    if (!p?.request) continue;
    const name = String(p.providerInfo?.name || p.isRobinhood || "");
    if (
      p.isRobinhood
      || p.isRobinhoodWallet
      || /robinhood/i.test(name)
      || /robinhood/i.test(String(p.providerInfo?.rdns || ""))
    ) {
      return { available: true, provider: p, name: "Robinhood Wallet" };
    }
  }
  // EIP-6963 announced providers may already be selected
  if (_selectedEvmProviderName && /robinhood/i.test(_selectedEvmProviderName)) {
    return { available: true, provider: _selectedEvmProvider, name: "Robinhood Wallet" };
  }
  return { available: false, provider: null, name: null };
}

export const ROBINHOOD_CHAIN_ID = 4663;

export async function getActiveEvmChainId() {
  const provider = defaultEvmProvider();
  if (!provider) return null;
  try {
    const hex = await provider.request({ method: "eth_chainId" });
    return parseInt(hex, 16);
  } catch {
    return null;
  }
}

/** True when connected wallet is not on Robinhood Chain (4663). */
export async function isWrongRobinhoodNetwork() {
  const id = await getActiveEvmChainId();
  if (id == null) return false;
  return id !== ROBINHOOD_CHAIN_ID;
}

// The EVM provider selected by the user (EIP-6963). Falls back to window.ethereum.
let _selectedEvmProvider = null;
let _selectedEvmProviderName = null;
export function setEvmProvider(p, name = null) {
  _selectedEvmProvider = p || null;
  _selectedEvmProviderName = p ? (name || null) : null;
}
let _selectedSolanaProviderName = null;
export function setSolanaProvider(name) {
  _selectedSolanaProviderName = name ? String(name).toLowerCase() : null;
}

function defaultEvmProvider() {
  if (typeof window === "undefined") return null;
  // The explicitly selected provider always wins.
  if (_selectedEvmProvider) return _selectedEvmProvider;
  const injected = window.ethereum;
  const providers = Array.isArray(injected?.providers) ? injected.providers : [];
  return providers.find((provider) => provider?.request) || (injected?.request ? injected : null);
}

function evmProvider() {
  const p = defaultEvmProvider();
  if (!p) throw new Error("No EVM wallet detected. Open FLIP inside your wallet app.");
  return p;
}
function solProvider(providerName = "Phantom") {
  const normalized = String(providerName || "").toLowerCase();
  const phantom = window.phantom?.solana || (window.solana?.isPhantom ? window.solana : null);
  const p = normalized === "solflare"
    ? window.solflare
    : normalized === "phantom"
      ? phantom
      : phantom || window.solflare;
  if (!p?.connect) throw new Error(`${providerName || "Solana"} wallet not detected.`);
  return p;
}

export async function connectEvm(providerOverride, { forcePicker = false } = {}) {
  if (providerOverride) _selectedEvmProvider = providerOverride;
  const provider = evmProvider();
  let account = null;
  if (forcePicker) {
    // Re-open the wallet's account picker even when permission was already
    // granted, so the user can choose a different account (MetaMask keeps
    // silently returning the first authorized account via eth_requestAccounts).
    try {
      await provider.request({
        method: "wallet_requestPermissions",
        params: [{ eth_accounts: {} }],
      });
    } catch (e) {
      if (e?.code === 4001) throw e; // user dismissed the picker
      // unsupported method on this provider → fall back to plain connect
    }
    const accounts = await provider.request({ method: "eth_accounts" });
    account = accounts?.[0];
  }
  if (!account) {
    [account] = await provider.request({ method: "eth_requestAccounts" });
  }
  // Report which wallet actually answered (may differ from the button label).
  const walletName = _selectedEvmProviderName
    || (provider.isPhantom ? "Phantom" : provider.isMetaMask ? "MetaMask"
      : provider.isCoinbaseWallet ? "Coinbase Wallet" : null);
  return { address: getAddress(account), ecosystem: "evm", walletName };
}

// Subscribe to account switches made inside the wallet. Listens on the
// selected provider when available, else on window.ethereum (which can be
// a different extension, e.g. Phantom, when several are installed).
// Returns an unsubscribe fn.
export function onEvmAccountsChanged(cb) {
  if (typeof window === "undefined") return () => {};
  const p = defaultEvmProvider() || window.ethereum;
  const target = p?.on && p?.removeListener ? p : null;
  if (!target) return () => {};
  const handler = (accounts) => cb(accounts);
  target.on("accountsChanged", handler);
  return () => {
    try { target.removeListener("accountsChanged", handler); } catch (e) {}
  };
}

export async function connectSolana(providerName = _selectedSolanaProviderName || "Phantom") {
  const provider = solProvider(providerName);
  const res = await provider.connect();
  const pk = res?.publicKey || provider.publicKey;
  setSolanaProvider(providerName);
  return { address: pk.toString(), ecosystem: "solana" };
}

// personal_sign with the selected (or default) EVM wallet. Used to prove
// profile ownership cross-device without any server-side session.
export async function signEvmMessage(message) {
  const provider = evmProvider();
  const [account] = await provider.request({ method: "eth_requestAccounts" });
  if (!account) throw new Error("No EVM account selected");
  const signature = await provider.request({ method: "personal_sign", params: [message, account] });
  return { address: account, signature };
}

// ---- WalletConnect: QR connection for 500+ mobile/hardware wallets ----
// Project ID is a public client identifier (dashboard.reown.com).
const WC_PROJECT_ID = process.env.REACT_APP_WC_PROJECT_ID || "811e1bf9ed6a9c8f1eb5dae449a8160f";
let _wcProvider = null;
let _wcInitPromise = null;

// WalletConnect normally exposes plain EVM addresses, but some mobile wallet
// versions briefly expose CAIP-10 account identifiers in the session object.
// Accept both forms so a successful approval is reflected in the UI.
export function extractWalletConnectAddress(provider) {
  const direct = provider?.accounts?.[0];
  if (typeof direct === "string") {
    const address = direct.includes(":") ? direct.split(":").pop() : direct;
    if (/^0x[0-9a-f]{40}$/i.test(address)) return address;
  }
  const sessionAccounts = provider?.session?.namespaces?.eip155?.accounts;
  if (Array.isArray(sessionAccounts)) {
    for (const account of sessionAccounts) {
      const address = typeof account === "string" && account.includes(":")
        ? account.split(":").pop() : account;
      if (/^0x[0-9a-f]{40}$/i.test(address)) return address;
    }
  }
  return null;
}

// WalletConnect Core is a per-page SINGLETON: initializing it more than once
// corrupts proposal/session state ("Init() was called N times", lost proposals,
// empty accounts). Always memoize — one provider instance per page load.
async function _getWC() {
  if (_wcProvider) return _wcProvider;
  if (!_wcInitPromise) {
    const { EthereumProvider } = await import("@walletconnect/ethereum-provider");
    _wcInitPromise = EthereumProvider.init({
      projectId: WC_PROJECT_ID,
      chains: [1], // required namespace; the rest is added via wallet_switchEthereumChain
      optionalChains: [8453, 42161, 10, 56, 137, 43114, 4663],
      showQrModal: true, // built-in modal: QR on desktop, deep-link on mobile
      metadata: {
        name: "FLIP",
        description: "Back your bag. Take the top.",
        url: window.location.origin,
        icons: [`${window.location.origin}/favicon.ico`],
      },
    }).then((p) => { _wcProvider = p; return p; });
  }
  return _wcInitPromise;
}

// Opens the QR modal, connects, and binds the WC provider as THE EVM provider —
// every existing flow (viem transfers, signing, chain switch) works unchanged.
export async function connectWalletConnect() {
  const provider = await _getWC();
  // A dead session left by a previous failed attempt makes connect() resolve
  // with zero accounts — drop it and start the proposal clean.
  if (provider.session) {
    try { await provider.disconnect(); } catch (e) { /* already dead */ }
  }
  try {
    await provider.connect();
    setEvmProvider(provider, "WalletConnect");
    let address = extractWalletConnectAddress(provider);
    if (!address) {
      // A few mobile wallets emit the session before updating the provider's
      // convenience `accounts` property.
      try {
        const accounts = await provider.request({ method: "eth_accounts" });
        const first = Array.isArray(accounts) ? accounts[0] : null;
        if (typeof first === "string" && /^0x[0-9a-f]{40}$/i.test(first)) address = first;
      } catch (e) { /* session diagnostics below provide the useful error */ }
    }
    if (!address) {
      console.error("[walletconnect] session namespaces:",
        JSON.stringify(provider.session?.namespaces || {}).slice(0, 600));
      // The WC Core is a page-level singleton: the only clean reset is a reload
      // with wiped storage. The page reloads itself — user retries once.
      Object.keys(localStorage).filter((k) => k.startsWith("wc@2") || k.includes("walletconnect"))
        .forEach((k) => localStorage.removeItem(k));
      toast.error("WalletConnect state was stale — page reloading, try connecting again");
      setTimeout(() => window.location.reload(), 1500);
      throw new Error("WalletConnect returned no account — page reloading for a clean retry");
    }
    return { address, ecosystem: "evm", walletName: "WalletConnect" };
  } catch (e) {
    // Full diagnostics in console — wallet-side failures (rejected proposal,
    // unsupported chain, wallet's WC toggle off) only surface here.
    console.error("[walletconnect] connect failed:", e?.message, { code: e?.code, data: e?.data });
    throw e;
  }
}

// After a page reload the WC session lives in localStorage: re-init silently and
// re-bind the provider if a session is still alive. Returns null otherwise.
export async function restoreWalletConnect() {
  try {
    const p = await _getWC();
    if (p.session && p.accounts?.[0]) {
      setEvmProvider(p, "WalletConnect");
      return { address: p.accounts[0] };
    }
    return null;
  } catch (e) {
    return null;
  }
}

export function isWalletConnectActive() {
  return !!_wcProvider && defaultEvmProvider() === _wcProvider;
}

export async function disconnectWalletConnect() {
  try {
    if (_wcProvider) await _wcProvider.disconnect();
  } catch (e) { /* session may already be gone */ }
  _wcProvider = null;
}

export async function switchEvmChain(chainKey) {
  const chain = VIEM_CHAINS[chainKey];
  if (!chain) throw new Error("Unsupported chain");
  const provider = evmProvider();
  const hexId = `0x${chain.id.toString(16)}`;
  try {
    await provider.request({ method: "wallet_switchEthereumChain", params: [{ chainId: hexId }] });
  } catch (e) {
    if (e.code === 4902) {
      await provider.request({
        method: "wallet_addEthereumChain",
        params: [{
          chainId: hexId, chainName: chain.name, nativeCurrency: chain.nativeCurrency,
          rpcUrls: [chain.rpcUrls.default.http[0]],
          blockExplorerUrls: chain.blockExplorers?.default ? [chain.blockExplorers.default.url] : [],
        }],
      });
    } else {
      throw e;
    }
  }
}

export async function readErc20Decimals(chainKey, token) {
  const chain = VIEM_CHAINS[chainKey];
  const pub = createPublicClient({ chain, transport: custom(evmProvider()) });
  try {
    const d = await pub.readContract({ address: getAddress(token), abi: erc20Abi, functionName: "decimals" });
    return Number(d);
  } catch (e) {
    return 18;
  }
}

// Sends an ERC-20 transfer of `token` to `treasury`. Returns { txHash, from }.
export async function sendEvmTokenTransfer({ chainKey, token, treasury, amountHuman, decimals }) {
  await switchEvmChain(chainKey);
  const provider = evmProvider();
  const [account] = await provider.request({ method: "eth_requestAccounts" });
  const chain = VIEM_CHAINS[chainKey];
  const wallet = createWalletClient({ account: getAddress(account), chain, transport: custom(provider) });
  const hash = await wallet.writeContract({
    address: getAddress(token), abi: erc20Abi, functionName: "transfer",
    args: [getAddress(treasury), parseUnits(String(amountHuman), decimals)],
    account: getAddress(account), chain,
  });
  return { txHash: hash, from: getAddress(account) };
}

// Sends a native coin (ETH/BNB/POL/AVAX) transfer to the treasury. Returns { txHash, from }.
export async function sendEvmNativeTransfer({ chainKey, treasury, amountHuman }) {
  await switchEvmChain(chainKey);
  const provider = evmProvider();
  const [account] = await provider.request({ method: "eth_requestAccounts" });
  const chain = VIEM_CHAINS[chainKey];
  const wallet = createWalletClient({ account: getAddress(account), chain, transport: custom(provider) });
  const hash = await wallet.sendTransaction({
    account: getAddress(account), chain,
    to: getAddress(treasury), value: parseEther(String(amountHuman)),
  });
  return { txHash: hash, from: getAddress(account) };
}

export async function sendSolNativeTransfer({ treasury, amountHuman }) {
  const providerName = _selectedSolanaProviderName || "Phantom";
  const provider = solProvider(providerName);
  const { address } = await connectSolana(providerName);
  // Blockhash comes from our backend: the public Solana RPC 403s from browsers
  // and the Alchemy key must stay server-side.
  const { blockhash, lastValidBlockHeight } = await apiSolBlockhash();
  const owner = new PublicKey(address);
  const tx = new Transaction({ recentBlockhash: blockhash, feePayer: owner }).add(
    SystemProgram.transfer({ fromPubkey: owner, toPubkey: new PublicKey(treasury),
      lamports: Math.round(Number(amountHuman) * LAMPORTS_PER_SOL) })
  );
  const result = await provider.signAndSendTransaction(tx);
  const signature = result.signature || result;
  // The wallet broadcasts the transaction; on-chain confirmation is done
  // server-side during verification (with retry for indexing lag).
  return { txHash: signature, from: address };
}


export async function getSolanaDecimals(mint) {
  const { decimals } = await apiSolMintDecimals(mint);
  return decimals;
}

// ---- Wallet balances (read-only, works with any connected provider) ----

// Public read-only RPCs: balance checks never touch the user's wallet, so we
// don't need chain switches and WalletConnect sessions stay untouched.
export const EVM_PUBLIC_RPC = {
  // llamarpс intermittently returns 521; PublicNode supports browser reads.
  ethereum: "https://ethereum-rpc.publicnode.com",
  base: "https://mainnet.base.org",
  arbitrum: "https://arb1.arbitrum.io/rpc",
  optimism: "https://mainnet.optimism.io",
  bnb: "https://bsc-dataseed.binance.org",
  // polygon-rpc.com now returns 403 (tenant disabled); use a public Polygon
  // RPC that supports browser JSON-RPC requests for portfolio reads.
  polygon: "https://polygon.drpc.org",
  avalanche: "https://api.avax.network/ext/bc/C/rpc",
  hyperliquid: "https://rpc.hyperliquid.xyz/evm",
  robinhood: "https://rpc.mainnet.chain.robinhood.com",
};

async function _rpcFetch(rpc, method, params) {
  const res = await fetch(rpc, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ jsonrpc: "2.0", id: 1, method, params }),
  });
  const body = await res.json();
  if (body.error) throw new Error(body.error.message || "RPC error");
  return body.result;
}

// When a wallet is connected through WalletConnect, read-only balance calls
// should use that same provider. This avoids browser blockers on public RPC
// hosts and keeps the balance on the wallet's active chain/session.
const EVM_CHAIN_IDS = {
  ethereum: 1, base: 8453, arbitrum: 42161, optimism: 10,
  bnb: 56, polygon: 137, avalanche: 43114, hyperliquid: 999,
  robinhood: 4663,
};

async function _rpcFetchWithActiveProvider(rpc, method, params, chainKey) {
  const provider = defaultEvmProvider();
  if (provider?.request) {
    try {
      const activeChain = await provider.request({ method: "eth_chainId" });
      const activeChainId = Number.parseInt(activeChain, 16);
      if (!chainKey || activeChainId === EVM_CHAIN_IDS[chainKey]) {
        const result = await provider.request({ method, params });
        if (result !== undefined && result !== null) return result;
      }
    } catch (e) {
      // Fall back to the public read-only RPC below.
    }
  }
  return _rpcFetch(rpc, method, params);
}

export async function getEvmNativeBalance(chainKey, address) {
  const rpc = EVM_PUBLIC_RPC[chainKey];
  if (!rpc) return null;
  const wei = await _rpcFetchWithActiveProvider(rpc, "eth_getBalance", [address, "latest"], chainKey);
  return Number(BigInt(wei || "0x0")) / 1e18;
}

export async function getEvmTokenBalance(chainKey, token, address, decimals) {
  const rpc = EVM_PUBLIC_RPC[chainKey];
  if (!rpc || !token) return null;
  const data = "0x70a08231" + "0".repeat(24) + address.toLowerCase().replace("0x", "");
  const raw = await _rpcFetchWithActiveProvider(rpc, "eth_call", [{ to: token, data }, "latest"], chainKey);
  return Number(BigInt(raw || "0x0")) / 10 ** decimals;
}

export async function getEvmTokenDecimals(chainKey, token) {
  const rpc = EVM_PUBLIC_RPC[chainKey];
  if (!rpc || !token) return 18;
  try {
    const raw = await _rpcFetchWithActiveProvider(rpc, "eth_call", [{ to: token, data: "0x313ce567" }, "latest"], chainKey);
    return Number(BigInt(raw || "0x12")); // 0x12 = 18
  } catch (e) {
    return 18;
  }
}

export async function getSolBalance(address) {
  const r = await apiSolBalance(address);
  return r.balance;
}

export async function getSolTokenBalance(owner, mint) {
  const r = await apiSolTokenBalance(owner, mint);
  return r.balance;
}

// Unified balance lookup for the payment modal. Returns units of the selected
// coin, or null when the chain/coin isn't balance-checkable.
export async function fetchCoinBalance({ method, chain, tokenContract, address }) {
  if (!address) return null;
  try {
    if (method === "native") {
      return chain === "solana" ? await getSolBalance(address) : await getEvmNativeBalance(chain, address);
    }
    // token / usdc / usdt — an SPL or ERC-20 balance
    if (!tokenContract) return null;
    if (chain === "solana") return await getSolTokenBalance(address, tokenContract);
    const decimals = await getEvmTokenDecimals(chain, tokenContract);
    return await getEvmTokenBalance(chain, tokenContract, address, decimals);
  } catch (e) {
    console.warn("[balance] lookup failed:", e?.message);
    return null;
  }
}

const PORTFOLIO_CHAINS = [
  ["ethereum", "ETH"], ["base", "ETH"], ["arbitrum", "ETH"],
  ["optimism", "ETH"], ["bnb", "BNB"], ["polygon", "POL"],
  ["avalanche", "AVAX"], ["hyperliquid", "HYPE"], ["robinhood", "ETH"],
];

// Total native-asset portfolio value across all supported EVM chains.
// Token balances are handled separately by payment flows; this deliberately
// keeps the portfolio read-only and free of approvals or chain switching.
export async function fetchEvmPortfolioBalance(address) {
  if (!address) return { usd: 0, chains: [] };
  const result = await getPortfolioBalance(address, "evm");
  return { usd: Number(result?.total_usd) || 0, chains: result?.assets || [], partial: result?.partial };
  /*
  const prices = new Map();
  const uniqueSymbols = [...new Set(PORTFOLIO_CHAINS.map(([, symbol]) => symbol))];
  await Promise.all(uniqueSymbols.map(async (symbol) => {
    try {
      const result = await pricePreview(symbol, 1);
      prices.set(symbol, Number(result?.price_usd) || 0);
    } catch (e) {
      prices.set(symbol, 0);
    }
  }));
  const balances = await Promise.all(PORTFOLIO_CHAINS.map(async ([chain, symbol]) => {
    try {
      const amount = await getEvmNativeBalance(chain, address);
      return { chain, symbol, amount: Number(amount) || 0, usd: (Number(amount) || 0) * (prices.get(symbol) || 0) };
    } catch (e) {
      return { chain, symbol, amount: 0, usd: 0 };
    }
  }));
  return {
    usd: balances.reduce((total, item) => total + item.usd, 0),
    chains: balances.filter((item) => item.amount > 0),
  };
  */
}

export async function fetchSolanaPortfolioBalance(address) {
  if (!address) return { usd: 0, chains: [] };
  const result = await getPortfolioBalance(address, "solana");
  return { usd: Number(result?.total_usd) || 0, chains: result?.assets || [], partial: result?.partial };
  /*
  const [balance, price] = await Promise.all([
    getSolBalance(address),
    pricePreview("SOL", 1),
  ]);
  const amount = Number(balance) || 0;
  const usd = amount * (Number(price?.price_usd) || 0);
  return {
    usd,
    chains: amount > 0 ? [{ chain: "solana", symbol: "SOL", amount, usd }] : [],
  };
  */
}

// Sends an SPL token transfer of `mint` to `treasury` wallet. Returns { txHash, from }.
export async function sendSolTokenTransfer({ mint, treasury, amountHuman, decimals }) {
  const providerName = _selectedSolanaProviderName || "Phantom";
  const provider = solProvider(providerName);
  const { address } = await connectSolana(providerName);
  const { blockhash } = await apiSolBlockhash();
  const owner = new PublicKey(address);
  const mintKey = new PublicKey(mint);
  const treasuryKey = new PublicKey(treasury);
  const source = await getAssociatedTokenAddress(mintKey, owner);
  const destination = await getAssociatedTokenAddress(mintKey, treasuryKey);
  const raw = BigInt(Math.round(Number(amountHuman) * 10 ** decimals));
  const tx = new Transaction({ recentBlockhash: blockhash, feePayer: owner }).add(
    createTransferCheckedInstruction(source, mintKey, destination, owner, raw, decimals, [], TOKEN_PROGRAM_ID)
  );
  const result = await provider.signAndSendTransaction(tx);
  const signature = result.signature || result;
  return { txHash: signature, from: address };
}
